/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/reports.tsx`.
 *
 * A REWRITE, and the cheapest of the family: **no new endpoint was written for
 * it.** The Next original made four vault calls in its render path —
 * `listPipelines()`, `pipelineReport(id)`, `activityReport()`, `funnelReport()`
 * (`shared-render-ownership.md` cause **A**). All four ALREADY have contracts and
 * are ALREADY mounted on this host:
 *
 *   listPipelines()   -> crm.pipelines.list         (routes/crm.ts:568)
 *   pipelineReport()  -> crm.reports.pipeline_summary (routes/crm.ts:970)
 *   activityReport()  -> crm.reports.activity_summary (routes/crm.ts:960)
 *   funnelReport()    -> crm.reports.funnel_summary   (routes/crm.ts:965)
 *
 * so this file composes the four rather than forking a live contract. (This is
 * the concrete payoff of RO-3-00b's premise correction: the claim that 0 of 40
 * `crm` ops were mounted was false — it is 40 of 40 — and that is verified by
 * these four resolving to real mounts under their CANONICAL export names.)
 *
 * Changes, and only these:
 *   4 inline vault reads     -> 4 `useApi(...)` calls
 *   `pipelineReport(primary.id)` -> a DEPENDENT read: `useApi(..., primary ? {…} : null)`
 *                                (SWR's null-key idiom; nothing fires until the
 *                                pipeline list resolves, which is what an
 *                                `await` gave the server component for free)
 *
 * Every table, column and stat cell is verbatim. The contracts are all mounted —
 * this component is live, not a stub.
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  StatCard,
  type DataTableColumn,
} from "@excellent/ui";
import { formatMoneyMinor } from "@excellent/ui/blocks/crm/format";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";

interface PipelineStageReportRow {
  stage_id: number;
  stage_name: string;
  count: number;
  total_minor: number;
  weighted_minor: number;
  currency: string;
}

interface ActivityOwnerRow {
  owner_actor_id: number | null;
  owner_label: string;
  calls: number;
  emails: number;
  sms: number;
  meetings: number;
  total: number;
}

export function ReportsView() {
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const pipelines = pipelinesData?.pipelines ?? [];
  const primary = pipelines.find((p) => p.is_default === 1) ?? pipelines[0];

  // Dependent read: the pipeline snapshot is keyed by the primary pipeline's id,
  // which is only known once the list resolves. `useApi(op, null)` is SWR's
  // "don't fetch yet" key — the sequencing the server component got from `await`.
  const { data: pipelineSnapData } = useApi(
    crm.reports.pipeline_summary,
    primary ? { query: { pipeline_id: primary.id } } : null,
  );
  const pipelineSnap =
    pipelineSnapData ?? { pipeline_id: 0, stages: [], forecast_minor_by_currency: {} };

  const { data: activityData } = useApi(crm.reports.activity_summary, { query: {} });
  const activity = activityData ?? { by_owner: [] };

  const { data: funnelData } = useApi(crm.reports.funnel_summary, { query: {} });
  const funnel =
    funnelData ?? {
      leads_created: 0,
      opportunities_created: 0,
      opportunities_won: 0,
      lead_to_opp_pct: 0,
      opp_to_won_pct: 0,
    };

  const pipelineColumns: DataTableColumn<PipelineStageReportRow>[] = [
    {
      key: "stage",
      header: "Stage",
      cellClassName: "text-foreground",
      render: (s) => s.stage_name,
    },
    {
      key: "count",
      header: "Count",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (s) => s.count,
    },
    {
      key: "total",
      header: "Total",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (s) => formatMoneyMinor(s.total_minor, s.currency),
    },
    {
      key: "weighted",
      header: "Weighted",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (s) => formatMoneyMinor(s.weighted_minor, s.currency),
    },
  ];

  const activityColumns: DataTableColumn<ActivityOwnerRow>[] = [
    {
      key: "owner",
      header: "Owner",
      cellClassName: "text-foreground",
      render: (r) => r.owner_label,
    },
    {
      key: "calls",
      header: "Calls",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (r) => r.calls,
    },
    {
      key: "emails",
      header: "Emails",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (r) => r.emails,
    },
    {
      key: "sms",
      header: "SMS",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (r) => r.sms,
    },
    {
      key: "meetings",
      header: "Meetings",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (r) => r.meetings,
    },
    {
      key: "total",
      header: "Total",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (r) => r.total,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Reports"
        description="Where your pipeline stands — forecast and funnel."
      />
      <div className="grid grid-cols-2 gap-3">
        <DataTable
          title="Pipeline forecast"
          subtitle={primary ? primary.name : undefined}
          rows={pipelineSnap.stages as PipelineStageReportRow[]}
          columns={pipelineColumns}
          rowKey={(s) => s.stage_id}
          empty="No pipeline data."
          footer={
            <span>
              Forecast:{" "}
              {Object.entries(pipelineSnap.forecast_minor_by_currency)
                .map(([c, m]) => formatMoneyMinor(m, c))
                .join(" + ") || "—"}
            </span>
          }
        />
        <Card>
          <CardHeader>
            <CardTitle>Funnel</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-2">
            <StatCard
              title="Leads created"
              value={funnel.leads_created.toString()}
            />
            <StatCard
              title="Opportunities"
              value={funnel.opportunities_created.toString()}
            />
            <StatCard title="Won" value={funnel.opportunities_won.toString()} />
            <StatCard title="Lead → Opp" value={`${funnel.lead_to_opp_pct}%`} />
            <StatCard title="Opp → Won" value={`${funnel.opp_to_won_pct}%`} />
          </CardContent>
        </Card>
      </div>
      <DataTable
        title="Activity by owner"
        rows={activity.by_owner as ActivityOwnerRow[]}
        columns={activityColumns}
        rowKey={(r) => r.owner_actor_id ?? -1}
        empty="No activity logged."
      />
    </div>
  );
}
